import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { Box, Flex, TablesGrid } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { ISelectValue } from 'constants/globalTypes'
import { AppDispatch, RootState } from 'store'
import {
  generateFoodIntents,
  getFoodIntentsPreview,
  reconcileFoodAttendance,
  recomputeFoodCoefficients,
  runFoodWindowCutoff,
} from 'features/food/foodSlice'
import { IFoodDietaryTagResponse, INextDayPreviewRow } from 'types/api/foodApi.interface'
import { useHasPermission } from 'hooks/useHasPermission'
import { foodProvisioningModeLabel } from '../foodPlanningData'
import { toast } from 'react-toastify'

const tomorrow = () => {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  d.setHours(0, 0, 0, 0)
  return d
}

const today = () => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

const formatOperationalDay = (d: Date) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const formatDietaryTagSet = (tags: IFoodDietaryTagResponse[]) => {
  if (!tags?.length) return 'Untagged'
  return tags.map((t) => t.code || t.name).filter(Boolean).join(' + ') || 'Untagged'
}

interface IKitchenSummaryRow {
  id: number
  name: string
  planned: number
  softCapacity: number | null
  modes: string
}

export const DashboardTab = () => {
  const dispatch = useDispatch<AppDispatch>()
  const canKitchen = useHasPermission('kitchen')
  const { nextDayPreview, mealWindows, logisticsKitchens, isLoading } = useSelector(
    (store: RootState) => store.food,
  )
  const [generateDay, setGenerateDay] = useState<Date>(tomorrow())
  const [generateWindowId, setGenerateWindowId] = useState<number | null>(null)
  const [previewDay, setPreviewDay] = useState<Date>(tomorrow())
  const [previewKitchenId, setPreviewKitchenId] = useState<number | null>(null)
  const [generating, setGenerating] = useState(false)
  const [sumWin, setSumWin] = useState<number | null>(null)
  const [opsDay, setOpsDay] = useState<Date>(today())
  const [opsWindowId, setOpsWindowId] = useState<number | null>(null)
  const [opsBusy, setOpsBusy] = useState(false)

  const refreshPreview = (day: Date, kitchenId?: number | null) => {
    dispatch(
      getFoodIntentsPreview({
        day: formatOperationalDay(day),
        kitchenId: kitchenId || undefined,
      }),
    )
  }

  useEffect(() => {
    refreshPreview(previewDay, previewKitchenId)
  }, [dispatch, previewDay, previewKitchenId])

  const windows = mealWindows.data || []

  useEffect(() => {
    if (!windows.length) {
      setSumWin(null)
      setOpsWindowId(null)
      return
    }
    const stillValid = sumWin != null && windows.some((w) => w.id === sumWin)
    if (!stillValid) setSumWin(windows[0].id)
    if (opsWindowId == null || !windows.some((w) => w.id === opsWindowId)) {
      setOpsWindowId(windows[0].id)
    }
  }, [windows, sumWin, opsWindowId])

  const handleGenerate = async () => {
    setGenerating(true)
    const resp = await dispatch(
      generateFoodIntents({
        operationalDay: formatOperationalDay(generateDay),
        windowDefId: generateWindowId || undefined,
      }),
    )
    setGenerating(false)
    if (resp.meta.requestStatus !== 'fulfilled') return
    setPreviewDay(generateDay)
    refreshPreview(generateDay, previewKitchenId)
  }

  const handleCutoff = async () => {
    if (!opsWindowId) {
      toast.warn('Meal window must be selected')
      return
    }
    setOpsBusy(true)
    const resp = await dispatch(
      runFoodWindowCutoff({
        operationalDay: formatOperationalDay(opsDay),
        windowDefId: opsWindowId,
      }),
    )
    setOpsBusy(false)
    if (resp.meta.requestStatus === 'fulfilled') {
      refreshPreview(opsDay, previewKitchenId)
    }
  }

  const handleReconcile = async () => {
    setOpsBusy(true)
    const resp = await dispatch(
      reconcileFoodAttendance({ operationalDay: formatOperationalDay(opsDay) }),
    )
    setOpsBusy(false)
    if (resp.meta.requestStatus === 'fulfilled') {
      refreshPreview(opsDay, previewKitchenId)
    }
  }

  const handleRecompute = async () => {
    setOpsBusy(true)
    await dispatch(recomputeFoodCoefficients())
    setOpsBusy(false)
  }

  const previewTotal = useMemo(
    () => (nextDayPreview || []).reduce((s, row) => s + (row.totalCount || 0), 0),
    [nextDayPreview],
  )

  const plannedByWindow = useMemo(() => {
    const map: { [windowId: number]: number } = {}
    ;(nextDayPreview || []).forEach((row) => {
      const id = row.windowDef?.id
      if (!id) return
      map[id] = (map[id] || 0) + (row.totalCount || 0)
    })
    return map
  }, [nextDayPreview])

  const kitchenSummaryRows: IKitchenSummaryRow[] = useMemo(() => {
    const byKitchen: {
      [kitchenId: number]: { name: string; planned: number; modes: { [mode: number]: number } }
    } = {}

    ;(nextDayPreview || []).forEach((row) => {
      if (sumWin != null && row.windowDef?.id !== sumWin) return
      const id = row.kitchen?.id
      if (!id) return
      if (!byKitchen[id]) {
        byKitchen[id] = { name: row.kitchen?.name || `Kitchen #${id}`, planned: 0, modes: {} }
      }
      byKitchen[id].planned += row.totalCount || 0
      byKitchen[id].modes[row.mode] = (byKitchen[id].modes[row.mode] || 0) + (row.totalCount || 0)
    })

    const kitchenMeta = logisticsKitchens.data || []
    const ids = Object.keys(byKitchen).map(Number)
    const allIds =
      canKitchen && kitchenMeta.length
        ? Array.from(new Set([...kitchenMeta.map((k) => k.id), ...ids]))
        : ids

    return allIds.map((id) => {
      const fromPreview = byKitchen[id]
      const meta = kitchenMeta.find((k) => k.id === id)
      return {
        id,
        name: fromPreview?.name || meta?.name || `Kitchen #${id}`,
        planned: fromPreview?.planned || 0,
        softCapacity: meta?.softCapacity ?? null,
        modes: fromPreview
          ? Object.entries(fromPreview.modes)
              .map(([mode, count]) => `${foodProvisioningModeLabel(Number(mode))} ×${count}`)
              .join(', ')
          : '—',
      }
    })
  }, [nextDayPreview, sumWin, logisticsKitchens.data, canKitchen])

  const windowOptions = [
    { label: 'All windows', value: 0 },
    ...windows.map((w) => ({ label: w.name, value: w.id })),
  ]
  const kitchenOptions = [
    { label: 'All kitchens', value: 0 },
    ...(logisticsKitchens.data || []).map((k) => ({ label: k.name, value: k.id })),
  ]
  const opsWindowOptions = windows.map((w) => ({ label: w.name, value: w.id }))

  const previewColumns: IHonestTableColumn<INextDayPreviewRow & { id: string }>[] = [
    { id: 'kitchen', header: 'Kitchen', cell: (row) => row.kitchen?.name || '—' },
    { id: 'window', header: 'Window', cell: (row) => row.windowDef?.name || '—' },
    { id: 'mode', header: 'Mode', cell: (row) => foodProvisioningModeLabel(row.mode) },
    { id: 'totalCount', header: 'Planned', cell: (row) => row.totalCount },
    {
      id: 'dietaryTagSets',
      header: 'Dietary combinations',
      minWidth: '16rem',
      cell: (row) => (
        <Flex wrap gap='0.25rem'>
          {(row.dietaryTagSets || []).map((set, idx) => (
            <StatusPill
              key={`${formatDietaryTagSet(set.dietaryTags)}-${idx}`}
              status='accent'
            >
              {`${formatDietaryTagSet(set.dietaryTags)} ×${set.count}`}
            </StatusPill>
          ))}
          {!row.dietaryTagSets?.length && <OtherText grey>—</OtherText>}
        </Flex>
      ),
    },
  ]

  const previewRows: (INextDayPreviewRow & { id: string })[] = (nextDayPreview || []).map(
    (row, idx) => ({
      ...row,
      id: `${row.kitchen?.id}-${row.windowDef?.id}-${row.mode}-${idx}`,
    }),
  )

  const summaryColumns: IHonestTableColumn<IKitchenSummaryRow>[] = [
    { id: 'name', header: 'Kitchen', cell: (row) => <BodyText weight='600'>{row.name}</BodyText> },
    { id: 'planned', header: 'Planned', cell: (row) => row.planned },
    {
      id: 'softCapacity',
      header: 'Soft capacity',
      cell: (row) => {
        if (row.softCapacity == null) return <OtherText grey>—</OtherText>
        const over = row.planned > row.softCapacity
        return (
          <StatusPill status={over ? 'danger' : 'success'}>
            {`${row.planned} / ${row.softCapacity}`}
          </StatusPill>
        )
      },
    },
    { id: 'modes', header: 'By mode', minWidth: '12rem', cell: (row) => row.modes },
  ]

  return (
    <Flex column gap='1rem' width='100%'>
      <TablesGrid>
        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Generate Intents</TitleText>
          </ContainerHeader>
          <ContainerContent gap='1rem'>
            <OtherText grey>
              Queues planning for an operational day from the work schedule. Also syncs — people
              removed from the schedule (or moved to an absence shift) before cut-off get their
              planned meals cancelled. Returns immediately; refresh the preview for results. The
              server also runs this automatically every minute.
            </OtherText>
            <Flex gap='1rem' wrap>
              <CalendarInput
                nomarg
                labelText='Operational day'
                defaultValue={generateDay}
                onChange={(d: Date) => d && setGenerateDay(d)}
              />
              <HonestSelectInput
                nomarg
                labelText='Meal window (optional)'
                options={windowOptions}
                defaultValue={generateWindowId || 0}
                onChange={(v: ISelectValue) =>
                  setGenerateWindowId(v && Number(v.value) ? Number(v.value) : null)
                }
                name='generateWindow'
              />
            </Flex>
            <Button variant='primary' disabled={generating} onClick={handleGenerate}>
              {generating ? 'Queuing…' : 'Generate Intents'}
            </Button>
          </ContainerContent>
        </Container>

        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Next-Day Preview</TitleText>
          </ContainerHeader>
          <ContainerContent gap='1rem'>
            <OtherText grey>
              Aggregated PLANNED counts only (name-free). After a window&apos;s cut-off fires,
              those meals lock to ORDERED and disappear from this preview — empty does not mean
              nothing was planned. Total planned: <b>{previewTotal}</b>
            </OtherText>
            <Flex gap='1rem' wrap>
              <CalendarInput
                nomarg
                labelText='Day'
                defaultValue={previewDay}
                onChange={(d: Date) => d && setPreviewDay(d)}
              />
              {canKitchen && (
                <HonestSelectInput
                  nomarg
                  labelText='Kitchen (optional)'
                  options={kitchenOptions}
                  defaultValue={previewKitchenId || 0}
                  onChange={(v: ISelectValue) =>
                    setPreviewKitchenId(v && Number(v.value) ? Number(v.value) : null)
                  }
                  name='previewKitchen'
                />
              )}
            </Flex>
            <MetricCard value={previewTotal} label='Total planned meals' />
          </ContainerContent>
        </Container>
      </TablesGrid>

      <Container>
        <ContainerHeader>
          <TitleText weight='700'>Ops triggers</TitleText>
        </ContainerHeader>
        <ContainerContent gap='1rem'>
          <OtherText grey>
            Manual overrides for testing and support. In normal operation cut-off, attendance
            reconcile, and schedule sync already run automatically every minute.
          </OtherText>
          <Flex gap='1rem' wrap>
            <CalendarInput
              nomarg
              labelText='Operational day'
              defaultValue={opsDay}
              onChange={(d: Date) => d && setOpsDay(d)}
            />
            <HonestSelectInput
              nomarg
              labelText='Meal window (for cut-off)'
              options={opsWindowOptions}
              defaultValue={opsWindowId || undefined}
              onChange={(v: ISelectValue) =>
                setOpsWindowId(v && Number(v.value) ? Number(v.value) : null)
              }
              name='opsWindow'
            />
          </Flex>
          <Flex gap='0.75rem' wrap>
            <Button variant='secondary' disabled={opsBusy || !opsWindowId} onClick={handleCutoff}>
              Run cut-off
            </Button>
            <Button variant='secondary' disabled={opsBusy} onClick={handleReconcile}>
              Reconcile attendance
            </Button>
            <Button variant='secondary' disabled={opsBusy} onClick={handleRecompute}>
              Recompute coefficients
            </Button>
          </Flex>
        </ContainerContent>
      </Container>

      <Flex gap='0.875rem' width='100%' wrap>
        {windows.map((w) => (
          <Box key={w.id} width='100%' style={{ minWidth: '12rem', flex: '1 1 12rem' }}>
            <Flex between center>
              <BodyText weight='700'>{w.name}</BodyText>
              <StatusPill status='info'>Planned</StatusPill>
            </Flex>
            <OtherText grey>
              {`${String(w.serviceStart || '').slice(0, 5)}–${String(w.serviceEnd || '').slice(0, 5)}`}
              {' · cut-off '}
              {String(w.cutoffTime || '').slice(0, 5)}
            </OtherText>
            <Flex column mt='12'>
              <TitleText weight='700'>{plannedByWindow[w.id] || 0}</TitleText>
              <OtherText grey>Still planned (unlocked) for selected day</OtherText>
            </Flex>
          </Box>
        ))}
        {!windows.length && <OtherText grey>No meal windows configured yet.</OtherText>}
      </Flex>

      <Container>
        <ContainerHeader>
          <TitleText weight='700'>Preview rows</TitleText>
        </ContainerHeader>
        <ContainerContent>
          <HonestTable
            columns={previewColumns}
            data={previewRows}
            keyField='id'
            loading={isLoading}
            emptyMessage='No PLANNED intents for this day — generate intents, or the window may already be locked past cut-off'
          />
        </ContainerContent>
      </Container>

      <Container>
        <ContainerHeader padding>
          <Flex between center width='100%' wrap gap='0.75rem'>
            <TitleText weight='700'>Kitchen planned summary</TitleText>
            <Flex gap='0.5rem' wrap>
              {windows.map((w) => (
                <Button
                  key={w.id}
                  size='sm'
                  variant={sumWin === w.id ? 'primary' : 'secondary'}
                  onClick={() => setSumWin(w.id)}
                >
                  {w.name}
                </Button>
              ))}
            </Flex>
          </Flex>
        </ContainerHeader>
        <ContainerContent>
          <HonestTable
            columns={summaryColumns}
            data={kitchenSummaryRows}
            keyField='id'
            emptyMessage='No kitchen planned counts for this window'
          />
        </ContainerContent>
      </Container>
    </Flex>
  )
}
